import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, HelpCircle } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';

const AssistantsPricing: React.FC = () => {
  const plans = [
    {
      name: 'Starter',
      price: '$46',
      period: '/month',
      setup: '$150 one-time setup',
      description: 'Perfect for small practices getting started with AI assistance.',
      features: [
        '1 governed AI assistant for 1 website',
        'Deployed across all pages of the website',
        'Up to 1,000 conversations per month',
        'Website chat widget integration',
        'Core Safety Governance Pack',
        'Email escalation support',
        'Monthly usage report',
      ],
      cta: 'Get Started',
      popular: false,
    },
    {
      name: 'Professional',
      price: '$86.25',
      period: '/month',
      setup: '$230 one-time setup',
      description: 'For growing firms managing multiple websites or higher volumes.',
      features: [
        'Up to 3 governed AI assistants (1 per website)',
        'Each assistant deployed across all pages of its website',
        'Up to 5,000 conversations per month (shared across assistants)',
        'Multi-channel deployment (website widget, secure links, internal dashboards)',
        'Custom Governance Pack per assistant',
        'Priority escalation handling',
        'Weekly governance & usage reports',
        'API access for integrations',
      ],
      cta: 'Get Started',
      popular: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      setup: 'Custom implementation',
      description: 'For large organisations with complex governance or integration requirements.',
      features: [
        'Unlimited governed AI assistants (1 per website)',
        'Unlimited conversations',
        'White-label options',
        'Custom governance & QA suites',
        'Dedicated account manager',
        'SLA-backed escalation handling',
        'Advanced analytics & audit trails',
        'Custom integrations and API access',
        'Multi-tenant support',
      ],
      cta: 'Contact Us',
      popular: false,
    },
  ];

  const faqs = [
    {
      question: 'What is a governed AI assistant?',
      answer: 'A managed AI assistant configured with governance rules, quality assurance testing, and escalation workflows. Each assistant is deployed to a single website and works across all pages of that site.',
    },
    {
      question: 'Can I deploy multiple assistants on one website?',
      answer: 'Each assistant is designed for a single website. If you need different assistants for different purposes, contact us to discuss your requirements.',
    },
    {
      question: 'What counts as a conversation?',
      answer: 'A conversation is a single chat session with your assistant, regardless of how many messages are exchanged within that session.',
    },
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer: 'Yes, you can change plans at any time. Changes take effect at the start of your next billing cycle.',
    },
    {
      question: 'What happens if I exceed my conversation limit?',
      answer: 'We\'ll notify you when you\'re approaching your limit. Additional conversations are billed at $0.10 each, or you can upgrade your plan.',
    },
    {
      question: 'What\'s included in the setup fee?',
      answer: 'Setup includes discovery sessions, content ingestion, custom training, governance configuration, testing, and deployment support.',
    },
    {
      question: 'Do you offer discounts for annual billing?',
      answer: 'Yes, annual billing includes a 15% discount. Contact us to discuss annual plans.',
    },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="bg-gradient-to-br from-brand-navy to-brand-blue py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
                Simple, Transparent Pricing
              </h1>
              <p className="text-xl text-white/90">
                Choose the plan that fits your practice. All plans include governance, 
                support, and the tools you need to succeed.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {plans.map((plan, index) => (
                <div 
                  key={index} 
                  className={`bg-card rounded-xl p-8 relative ${
                    plan.popular 
                      ? 'border-2 border-brand-teal shadow-lg' 
                      : 'border border-border'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-brand-teal text-white text-sm font-medium px-4 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-card-foreground mb-2">{plan.name}</h3>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-card-foreground">{plan.price}</span>
                      <span className="text-muted-foreground">{plan.period}</span>
                    </div>
                    <p className="text-sm text-brand-teal font-medium">{plan.setup}</p>
                  </div>
                  <p className="text-muted-foreground text-sm mb-6 text-center">{plan.description}</p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm">
                        <CheckCircle className="h-5 w-5 text-brand-teal flex-shrink-0" />
                        <span className="text-card-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/assistants/get-assistant" className="block">
                    <Button 
                      className={`w-full ${plan.popular ? '' : 'bg-card-foreground hover:bg-card-foreground/90'}`}
                      variant={plan.popular ? 'default' : 'outline'}
                    >
                      {plan.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 lg:py-24 bg-muted/50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="h-5 w-5 text-brand-teal flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-card-foreground mb-2">{faq.question}</h3>
                      <p className="text-muted-foreground text-sm">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-20 bg-brand-navy">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Not Sure Which Plan Is Right?
            </h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Let's discuss your needs. We'll recommend the best solution for your practice.
            </p>
            <Link to="/assistants/get-assistant">
              <Button size="lg" className="bg-brand-teal hover:bg-brand-teal/90">
                Schedule a Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AssistantsPricing;
